import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DynamicProfileVisualizer } from "../Components/DynamicQuestionnaire/client";
import { ChevronLeft, Download } from "lucide-react";

/**
 * ProfilePreviewPage Component
 *
 * A full page preview for the Dynamic Profile Visualizer
 * Can be accessed by project ID, response ID, or session storage preview data
 */
const ProfilePreviewPage = () => {
  const { projectId, responseId } = useParams();
  const navigate = useNavigate();
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Try to get preview data from session storage first
    const sessionData = sessionStorage.getItem("previewProfileData");
    if (sessionData) {
      try {
        const parsedData = JSON.parse(sessionData);
        setPreviewData(parsedData);
        setLoading(false);
        // Clear the session storage so it doesn't persist indefinitely
        sessionStorage.removeItem("previewProfileData");
        return;
      } catch (err) {
        console.error("Error parsing preview data from session storage:", err);
        // Continue with normal flow if session data is invalid
      }
    }

    // If no preview data in session storage, we will rely on the route params
    setLoading(false);
  }, []);

  const goBack = () => {
    navigate(-1); // Go back to previous page
  };

  // Function to capture the profile as PDF (placeholder - would need implementation)
  const downloadAsPdf = () => {
    alert("PDF download functionality would be implemented here");
    // This would require a library like html2pdf or a server-side solution
  };

  return (
    <div className="profile-preview-page bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-gray-900 border-b border-gray-800 shadow-md">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={goBack}
            className="flex items-center text-white hover:text-blue-400 transition-colors"
          >
            <ChevronLeft size={20} className="mr-1" />
            Back
          </button>

          <h1 className="text-white text-xl font-medium">
            Brand Profile Preview
          </h1>

          <button
            onClick={downloadAsPdf}
            className="flex items-center px-3 py-1 bg-purple-600 hover:bg-purple-700 rounded text-white text-sm"
          >
            <Download size={16} className="mr-1" />
            Download PDF
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-6">
        {loading ? (
          <div className="flex justify-center items-center py-32">
            <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full"></div>
          </div>
        ) : error ? (
          <div className="bg-red-900/20 border border-red-500 rounded-lg p-6 max-w-2xl mx-auto">
            <h3 className="text-red-500 text-xl font-medium mb-2">
              Error Loading Preview
            </h3>
            <p className="text-white/70">{error}</p>
            <button
              onClick={goBack}
              className="mt-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded text-white"
            >
              Go Back
            </button>
          </div>
        ) : previewData ? (
          <DynamicProfileVisualizer responseId={previewData.id} />
        ) : (
          <DynamicProfileVisualizer
            projectId={projectId}
            responseId={responseId}
          />
        )}
      </div>
    </div>
  );
};

export default ProfilePreviewPage;
